// @ts-nocheck
import { useEffect, useMemo, useState } from 'react';
import { ownerApi } from './ownerApi';
import { useLoad } from './lib/useLoad';
import Card from './ui/Card';
import Button from './ui/Button';
import Alert from './ui/Alert';

const FIELDS = ['input', 'cached', 'output', 'reasoning', 'audio', 'image', 'audio_per_hour'];

export default function OwnerModelPrices() {
  const state = useLoad(
    () => (typeof ownerApi.modelPrices === 'function' ? ownerApi.modelPrices() : Promise.resolve(null)),
    [],
  );
  const [models, setModels] = useState(/** @type {Record<string, any> | null} */ (null));
  const [query, setQuery] = useState('grok');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState('');

  useEffect(() => {
    if (state.status === 'ready' && state.data?.models) setModels(state.data.models);
  }, [state.status, state.data]);

  const names = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return Object.keys(models || {}).filter((name) => !needle || name.toLowerCase().includes(needle)).sort().slice(0, 40);
  }, [models, query]);

  if (state.status === 'ready' && !state.data) return null;
  if (!models) return null;

  const edit = (name, field, value) => {
    setSaved('');
    setModels((current) => ({
      ...current,
      [name]: { ...current[name], [field]: value },
    }));
  };

  const save = async () => {
    setBusy(true);
    setError('');
    setSaved('');
    /** @type {Record<string, Record<string, number>>} */
    const body = {};
    for (const [name, row] of Object.entries(models)) {
      /** @type {Record<string, number>} */
      const cleaned = { input: Number(row.input || 0), output: Number(row.output || 0) };
      for (const field of FIELDS) {
        if (field === 'input' || field === 'output') continue;
        if (row[field] == null || row[field] === '') continue;
        cleaned[field] = Number(row[field]);
      }
      body[name] = cleaned;
    }
    try {
      const data = await ownerApi.saveModelPrices(body);
      setModels(data.models || models);
      setSaved('Prices saved. The next message uses them.');
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Model prices (USD per 1M tokens)" subtitle="xAI defaults come from the official price list. Speech-to-text uses USD per hour.">
      {error ? <Alert title="Prices were not saved." detail={error} /> : null}
      {saved ? <p className="mb-3 text-sm text-green-800">{saved}</p> : null}
      <label className="mb-3 block text-sm">Find a model
        <input aria-label="Find a model" className="mt-1 h-9 w-full max-w-sm rounded-lg border border-[#7C8798] px-3" value={query} onChange={(event) => setQuery(event.target.value)} />
      </label>
      <div className="space-y-4">
        {names.map((name) => (
          <div key={name} className="rounded-lg border border-slate-200 p-3">
            <p className="text-sm font-medium">{name}</p>
            <div className="mt-2 grid grid-cols-2 gap-2 md:grid-cols-4">
              {FIELDS.filter((field) => field === 'input' || field === 'output' || models[name][field] != null).map((field) => (
                <label key={field} className="text-[13px] text-slate-600">{field}
                  <input aria-label={`${name} ${field}`} className="mt-1 h-9 w-full rounded-lg border border-[#7C8798] px-2 text-slate-900" value={models[name][field] ?? ''} onChange={(event) => edit(name, field, event.target.value)} />
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4"><Button onClick={() => void save()} disabled={busy}>{busy ? 'Saving…' : 'Save prices'}</Button></div>
    </Card>
  );
}
